import {
  DEFAULT_THEME_SCHEDULE,
  type ResolvedTheme,
  type ThemePreference,
  type ThemeSchedule,
} from '../types/ThemeType';

const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

export const parseTimeToMinutes = (time: string): number | null => {
  const match = TIME_PATTERN.exec(time);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
};

export const normalizeThemeSchedule = (schedule?: Partial<ThemeSchedule> | null): ThemeSchedule => ({
  lightFrom: schedule?.lightFrom ?? DEFAULT_THEME_SCHEDULE.lightFrom,
  darkFrom: schedule?.darkFrom ?? DEFAULT_THEME_SCHEDULE.darkFrom,
});

export const isScheduleDark = (schedule: ThemeSchedule, now: Date = new Date()): boolean => {
  const lightStart = parseTimeToMinutes(schedule.lightFrom);
  const darkStart = parseTimeToMinutes(schedule.darkFrom);
  if (lightStart === null || darkStart === null) {
    return false;
  }

  const nowMinutes = now.getHours() * 60 + now.getMinutes();

  if (lightStart < darkStart) {
    return nowMinutes < lightStart || nowMinutes >= darkStart;
  }

  return nowMinutes >= darkStart && nowMinutes < lightStart;
};

export type ResolveThemePreferenceParams = {
  preference: ThemePreference;
  schedule?: Partial<ThemeSchedule> | null;
  now?: Date;
  systemDark?: boolean;
};

export const resolveThemePreference = ({
  preference,
  schedule,
  now = new Date(),
  systemDark = false,
}: ResolveThemePreferenceParams): ResolvedTheme => {
  switch (preference) {
    case 'light':
      return 'light';
    case 'dark':
      return 'dark';
    case 'system':
      return systemDark ? 'dark' : 'light';
    case 'schedule':
      return isScheduleDark(normalizeThemeSchedule(schedule), now) ? 'dark' : 'light';
    default:
      return 'light';
  }
};

export const generateThemeTimeOptions = (): string[] => {
  const options: string[] = [];
  for (let hour = 0; hour < 24; hour += 1) {
    for (const minute of [0, 30]) {
      options.push(`${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
    }
  }
  return options;
};

export const THEME_CHECK_INTERVAL_MS = 60 * 1000;
